import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { colors, buttonBase } from "./theme.js";
// ── Context ──
const ConfirmContext = createContext(null);
export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        throw new Error("useConfirm must be used within a ConfirmProvider");
    }
    return ctx;
}
// ── Provider ──
export function ConfirmProvider({ children }) {
    const [request, setRequest] = useState(null);
    const resolveRef = useRef(null);
    const confirm = useCallback((title, message, options = {}) => {
        return new Promise((resolve) => {
            resolveRef.current = resolve;
            setRequest({ title, message, options, resolve });
        });
    }, []);
    function handleResponse(result) {
        resolveRef.current?.(result);
        resolveRef.current = null;
        setRequest(null);
    }
    return (_jsxs(ConfirmContext.Provider, { value: { confirm }, children: [children, request && (_jsx(ConfirmDialog, { title: request.title, message: request.message, options: request.options, onConfirm: () => handleResponse(true), onCancel: () => handleResponse(false) }))] }));
}
// ── Dialog ──
function ConfirmDialog({ title, message, options, onConfirm, onCancel, }) {
    const isDanger = options.danger ?? false;
    const confirmText = options.confirmText ?? (isDanger ? "Delete" : "Confirm");
    const cancelText = options.cancelText ?? "Cancel";
    return (_jsx("div", { style: overlayStyle, onClick: onCancel, children: _jsxs("div", { style: dialogStyle, onClick: (e) => e.stopPropagation(), children: [_jsx("div", { style: { fontWeight: 700, fontSize: "0.95rem", color: colors.text, marginBottom: 6 }, children: title }), _jsx("div", { style: { fontSize: "0.85rem", color: colors.textSecondary, lineHeight: 1.5, marginBottom: 20 }, children: message }), _jsxs("div", { style: { display: "flex", justifyContent: "flex-end", gap: 8 }, children: [_jsx("button", { onClick: onCancel, style: cancelButtonStyle, children: cancelText }), _jsx("button", { onClick: onConfirm, autoFocus: true, style: {
                                ...buttonBase,
                                background: isDanger ? colors.danger : colors.primary,
                                color: colors.surface,
                            }, children: confirmText })] })] }) }));
}
// ── Styles ──
const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.4)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    animation: "confirm-fade-in 150ms ease",
};
const dialogStyle = {
    background: colors.surface,
    borderRadius: 12,
    padding: 20,
    maxWidth: 360,
    width: "100%",
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
    animation: "confirm-scale-in 150ms ease",
};
const cancelButtonStyle = {
    ...buttonBase,
    background: "none",
    border: `1px solid ${colors.border}`,
    color: colors.textSecondary,
};
// ── Keyframe injection (runs once) ──
const STYLE_ID = "confirm-keyframes";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
    @keyframes confirm-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes confirm-scale-in {
      from { opacity: 0; transform: scale(0.95); }
      to   { opacity: 1; transform: scale(1); }
    }
  `;
    document.head.appendChild(style);
}
